import React, {useEffect, useMemo, useState} from "react";
import '../../../css/web/AdminTaxListModal.css';
import '../../../css/web/AdminRenewTaxPg.css';
import commonClose from '../../../assets/img/commonClose.png';

/**
 * [부가세탭] 세무 데이터 필터링 팝업 (AdminRenewTaxPg 전용)
 *
 * - 기존 AdminTaxListModal 의 UI/양식(개별·전체 체크박스 / 인덱스 / 박스번호 / 성함 / 배송사 / 총배송료 / 운송장번호)을 유지하되,
 *   요구사항에 따라 [배송사별 탭버튼 필터] 와 [검색폼 필터] 를 추가한 신규 컴포넌트다.
 * - 기존 AdminTaxListModal 은 AdminTaxPg 에서 그대로 사용중이므로 수정하지 않는다.
 */

// 배송사코드(dvType) → 사용자 용어 변환
const convertToShipco = (dvType, defaultShipco) => {
    if (dvType === 'D00') return '국내배송';
    if (dvType === 'D01') return 'FedEx';
    if (dvType === 'D16') return 'FedEx-E';
    if (dvType === 'D11') return 'UPS';
    if (dvType === 'D17') return 'UPS-E';
    if (dvType === 'D02') return 'EMS';
    if (dvType === 'D15') return 'EMSP';
    if (dvType === 'D04') return 'DHL';
    if (dvType === 'D13') return 'K-Packet';
    if (dvType === 'D14') return 'Small-Packet';
    if (dvType === 'D03') return 'SEA';
    return defaultShipco || '';
};

// 배송사 탭버튼 목록(전체 + 실제 데이터에 존재하는 배송사만 노출)
const SHIPCO_TAB_ORDER = ['D01', 'D16', 'D11', 'D17', 'D02', 'D15', 'D13', 'D14', 'D03', 'D00', 'D04'];

function AdminRenewTaxFilterModal({show, pt1Data, closePopup, completeFilter}) {

    const [checkedItems, setCheckedItems] = useState(new Set());
    const [shipcoTab, setShipcoTab] = useState('ALL');
    const [searchKeyword, setSearchKeyword] = useState('');

    // 팝업이 새로 열릴 때마다 전체선택 상태로 초기화
    useEffect(() => {
        if (show && pt1Data && pt1Data.length > 0) {
            setCheckedItems(new Set(pt1Data.map((item) => item.paySeqNum)));
            setShipcoTab('ALL');
            setSearchKeyword('');
        }
    }, [show, pt1Data]);

    // 데이터에 실제 존재하는 배송사만 탭으로 구성
    const shipcoTabs = useMemo(() => {
        const exists = new Set((pt1Data || []).map((item) => item.dvType).filter(Boolean));
        const ordered = SHIPCO_TAB_ORDER.filter((code) => exists.has(code));
        return ['ALL', ...ordered];
    }, [pt1Data]);

    // 배송사탭 + 검색어 필터 적용된 목록
    const visibleData = useMemo(() => {
        const keyword = searchKeyword.trim().toLowerCase();
        // 총배송료 검색은 쉼표없이 숫자로만 비교
        const numericKeyword = keyword.replace(/[^0-9]/g, '');

        return (pt1Data || []).filter((item) => {
            if (shipcoTab !== 'ALL' && item.dvType !== shipcoTab) return false;
            if (!keyword) return true;

            const boxNo = `${item.orderNumber || ''}-${item.boxNum || ''}`.toLowerCase();
            const name = (item.regNm || '').toLowerCase();
            const shipco = convertToShipco(item.dvType, item.shipco).toLowerCase();
            const waybill = (item.outWayNum || '').toLowerCase();
            const shipExpense = String(item.ttShipExpense || 0);

            return boxNo.includes(keyword)
                || name.includes(keyword)
                || shipco.includes(keyword)
                || waybill.includes(keyword)
                || (numericKeyword !== '' && shipExpense.includes(numericKeyword));
        });
    }, [pt1Data, shipcoTab, searchKeyword]);

    // 전체선택 체크박스는 "현재 보이는 목록" 기준으로 동작
    const isAllVisibleChecked = visibleData.length > 0 && visibleData.every((item) => checkedItems.has(item.paySeqNum));

    const handleSelectAllVisible = (e) => {
        const {checked} = e.target;
        const next = new Set(checkedItems);
        visibleData.forEach((item) => {
            if (checked) next.add(item.paySeqNum);
            else next.delete(item.paySeqNum);
        });
        setCheckedItems(next);
    };

    const handleSelectItem = (paySeqNum) => {
        const next = new Set(checkedItems);
        if (next.has(paySeqNum)) next.delete(paySeqNum);
        else next.add(paySeqNum);
        setCheckedItems(next);
    };

    const handleCompleteFilter = () => {
        completeFilter((pt1Data || []).filter((item) => checkedItems.has(item.paySeqNum)));
    };

    if (!show) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h3>세무 데이터 필터링</h3>
                    <img className="close-icon" src={commonClose} alt="close" onClick={closePopup}/>
                </div>

                {/* 배송사별 탭버튼 필터 + 검색폼 */}
                <div style={{padding: '14px 20px 6px', borderBottom: '1px solid rgba(150,150,150,.2)'}}>
                    <div className="rtx-subtabs" style={{flexWrap: 'wrap', marginBottom: '12px', gap: '6px'}}>
                        {shipcoTabs.map((code) => {
                            const label = code === 'ALL' ? '전체' : convertToShipco(code, code);
                            const count = code === 'ALL'
                                ? (pt1Data || []).length
                                : (pt1Data || []).filter((item) => item.dvType === code).length;
                            return (
                                <div key={code}
                                     className={`rtx-subtab${shipcoTab === code ? ' is-active' : ''}`}
                                     style={{minWidth: 'auto', height: '32px', padding: '0 14px', fontSize: '13px'}}
                                     onClick={() => setShipcoTab(code)}>
                                    {label}
                                    <span className="rtx-subtab-badge">{count.toLocaleString()}</span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="display-flex align-center" style={{gap: '10px'}}>
                        <input
                            className="admin-input-line ht-34 font-s-13 p-l-10"
                            style={{flex: 1, borderRadius: '8px', border: '1px solid #dcdcdc'}}
                            type="text"
                            placeholder="박스번호 / 성함 / 배송사 / 총배송료(숫자만) / 운송장번호 검색"
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                        />
                        {searchKeyword && (
                            <button className="admin-btn-B wd-70 ht-32 font-s-12"
                                    onClick={() => setSearchKeyword('')}>초기화
                            </button>
                        )}
                        <span style={{fontSize: '12px', color: '#9a9a9a', whiteSpace: 'nowrap'}}>
                            {visibleData.length.toLocaleString()} / {(pt1Data || []).length.toLocaleString()}건 표시
                        </span>
                    </div>
                </div>

                <div className="modal-body">
                    <div className="table-container">
                        <table className="tax-table">
                            <thead>
                            <tr>
                                <th style={{width: '5%'}}>
                                    <input type="checkbox" checked={isAllVisibleChecked} onChange={handleSelectAllVisible}/>
                                </th>
                                <th style={{width: '5%'}}></th>
                                <th style={{width: '25%'}}>박스번호</th>
                                <th style={{width: '15%'}}>성함</th>
                                <th style={{width: '15%'}}>배송사</th>
                                <th style={{width: '15%'}}>총배송료</th>
                                <th style={{width: '20%'}}>운송장번호</th>
                            </tr>
                            </thead>
                            <tbody>
                            {visibleData.map((item, index) => (
                                <tr key={item.paySeqNum || index}>
                                    <td>
                                        <input type="checkbox"
                                               checked={checkedItems.has(item.paySeqNum)}
                                               onChange={() => handleSelectItem(item.paySeqNum)}/>
                                    </td>
                                    <td>{index + 1}</td>
                                    <td>{`${item.orderNumber || ''}-${item.boxNum || ''}`}</td>
                                    <td>{item.regNm}</td>
                                    <td>{convertToShipco(item.dvType, item.shipco)}</td>
                                    <td>{(item.ttShipExpense || 0).toLocaleString()}원</td>
                                    <td>{item.outWayNum}</td>
                                </tr>
                            ))}
                            {visibleData.length === 0 && (
                                <tr>
                                    <td colSpan={7} style={{padding: '40px 0', color: '#9a9a9a'}}>
                                        조건에 맞는 데이터가 없습니다.
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="filter-complete-btn" onClick={handleCompleteFilter}>
                        필터완료 ({checkedItems.size.toLocaleString()}개 선택됨)
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AdminRenewTaxFilterModal;
